import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { applyTheme, readPersistedTheme, watchSystemPreference, type Theme } from '../lib/theme';
import {
  browserPermission,
  disableNotifications,
  enableNotifications,
  reconcileDevice,
  sendTestNotification,
  type TestNotificationResult,
} from '../lib/onesignal';
import { EmailDomainModal } from '../components/EmailDomainModal';
import { PasskeyModal } from '../components/PasskeyModal';
import type { GoogleConnectionStatus, WaSessionInfo } from '../types';

const THEMES: Theme[] = ['system', 'light', 'dark'];

type NotificationPrefs = {
  notifyNewLead: boolean;
  notifyWaMessage: boolean;
  notifySigned: boolean;
  notifyInvoicePaid: boolean;
  notifyCalendarReminder: boolean;
  notifyLeadReminder: boolean;
  deviceCount: number;
};
type NotificationDevice = {
  id: string;
  deviceType: string | null;
  userAgent: string | null;
  subscriptionIdShort: string | null;
  oneSignalUserIdShort: string | null;
  lastSeenAt: string;
  createdAt: string;
};
const NOTIFICATION_KEYS = [
  'notifyNewLead',
  'notifyWaMessage',
  'notifySigned',
  'notifyInvoicePaid',
  'notifyCalendarReminder',
  'notifyLeadReminder',
] as const;
type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

/** Pull a short, human-readable browser/OS hint out of a UA string for
 *  the device list. We don't ship a full UA parser — first matching
 *  token wins, falls through to "Browser" so we never display a 200-
 *  char raw UA in the row.
 */
function shortUA(ua: string): string {
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Chrome/i.test(ua)) return 'Chrome';
  if (/Safari/i.test(ua)) return 'Safari';
  return 'Browser';
}

/**
 * One place for everything user-level: theme, language, integrations
 * (Google + WhatsApp + email-sender domain + passkeys). Replaces the
 * cluster of buttons that used to live in the sidebar UserMenu.
 */
export function Settings({
  firmId,
  onConnectWa,
}: {
  firmId: string;
  onConnectWa: () => void;
}) {
  const { t, i18n } = useTranslation(['settings', 'google', 'wa', 'email', 'passkey', 'common']);
  const { user: fbUser, signOut } = useAuth();

  const [theme, setTheme] = useState<Theme>(readPersistedTheme());
  const [savedTheme, setSavedTheme] = useState<Theme>(theme);
  const [google, setGoogle] = useState<GoogleConnectionStatus | null>(null);
  const [waInfo, setWaInfo] = useState<WaSessionInfo | null>(null);
  const [emailDomainOpen, setEmailDomainOpen] = useState(false);
  const [passkeyOpen, setPasskeyOpen] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs | null>(null);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>(
    'default',
  );
  const [busy, setBusy] = useState(false);
  const [erasePhase, setErasePhase] = useState<'idle' | 'confirm' | 'code'>('idle');
  const [eraseCode, setEraseCode] = useState('');
  const [eraseError, setEraseError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [lastTest, setLastTest] = useState<TestNotificationResult | null>(null);
  const [devices, setDevices] = useState<NotificationDevice[]>([]);

  // Reapply 'system' theme when the OS toggles light↔dark.
  useEffect(() => {
    const off = watchSystemPreference(() => {
      if (savedTheme === 'system') applyTheme('system');
    });
    return off;
  }, [savedTheme]);

  // Pull current preferences + integration statuses
  useEffect(() => {
    apiFetch<{ user: { theme: string } }>('/api/me')
      .then((d) => {
        const t = d.user.theme as Theme;
        if (THEMES.includes(t)) {
          setTheme(t);
          setSavedTheme(t);
          applyTheme(t);
        }
      })
      .catch(() => {});
    apiFetch<GoogleConnectionStatus>('/api/auth/google/status')
      .then(setGoogle)
      .catch(() => setGoogle(null));
    apiFetch<NotificationPrefs>('/api/notifications/preferences')
      .then(async (prefs) => {
        setNotifPrefs(prefs);
        // If we already have a device row, silently sync the SDK's
        // current (oneSignalUserId, subscriptionId) with the server —
        // catches stale rows from cleared browser data / SW rotation.
        // Best-effort: never blocks UI, never throws.
        if (prefs.deviceCount > 0) {
          try {
            const res = await reconcileDevice();
            if (res.status === 'reconciled' && res.deleted > 0) {
              const refreshed = await apiFetch<NotificationPrefs>(
                '/api/notifications/preferences',
              );
              setNotifPrefs(refreshed);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[onesignal-reconcile] failed', err);
          }
          void loadDevices();
        }
      })
      .catch(() => setNotifPrefs(null));
    void browserPermission().then(setNotifPermission);
    apiFetch<WaSessionInfo>(`/api/firms/${firmId}/wa/status`)
      .then(setWaInfo)
      .catch(() => setWaInfo(null));
  }, [firmId]);

  async function pickTheme(next: Theme) {
    setTheme(next);
    applyTheme(next); // immediate visual feedback; persist below
    setBusy(true);
    setMsg(null);
    try {
      await apiFetch('/api/me', {
        method: 'PATCH',
        body: JSON.stringify({ theme: next }),
      });
      setSavedTheme(next);
      setMsg(t('saved'));
    } catch {
      // Server save failed — keep the local apply (user still gets the
      // visual). Will retry on next change.
    } finally {
      setBusy(false);
    }
  }

  async function pickLocale(next: 'he' | 'en') {
    if (i18n.language === next) return;
    void i18n.changeLanguage(next);
    setBusy(true);
    setMsg(null);
    try {
      await apiFetch('/api/me', {
        method: 'PATCH',
        body: JSON.stringify({ defaultLocale: next }),
      });
      setMsg(t('saved'));
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  }

  async function connectGoogle() {
    setBusy(true);
    try {
      const { url } = await apiFetch<{ url: string }>('/api/auth/google/start', {
        method: 'POST',
      });
      window.location.href = url;
    } catch {
      setBusy(false);
    }
  }

  async function disconnectGoogle() {
    if (!confirm(t('google:disconnect_confirm'))) return;
    setBusy(true);
    try {
      await apiFetch('/api/auth/google', { method: 'DELETE' });
      const s = await apiFetch<GoogleConnectionStatus>('/api/auth/google/status');
      setGoogle(s);
    } finally {
      setBusy(false);
    }
  }

  // Google Contacts toggles independently of calendar. Enabling when the
  // contacts scope was never granted re-runs the OAuth flow (which grants
  // the bundled calendar+contacts scopes); otherwise it just flips the
  // server-side flag. Disabling never touches the calendar connection.
  async function connectContacts() {
    if (!google?.connected) return connectGoogle();
    if (!google.hasContacts) return connectGoogle(); // re-consent to add scope
    setBusy(true);
    try {
      await apiFetch('/api/auth/google/contacts', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: true }),
      });
      const s = await apiFetch<GoogleConnectionStatus>('/api/auth/google/status');
      setGoogle(s);
    } catch (err) {
      // Scope vanished server-side — fall back to a full re-consent.
      if (err instanceof ApiError && err.code === 'scope_missing') {
        return connectGoogle();
      }
    } finally {
      setBusy(false);
    }
  }

  async function disconnectContacts() {
    if (!confirm(t('google:contacts_disconnect_confirm'))) return;
    setBusy(true);
    try {
      await apiFetch('/api/auth/google/contacts', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: false }),
      });
      const s = await apiFetch<GoogleConnectionStatus>('/api/auth/google/status');
      setGoogle(s);
    } finally {
      setBusy(false);
    }
  }

  // Google Drive document sync — same connect/disconnect pattern as
  // contacts. Enabling without the drive.file scope granted re-runs OAuth.
  async function connectDrive() {
    if (!google?.connected || !google.hasDrive) return connectGoogle();
    setBusy(true);
    try {
      await apiFetch('/api/auth/google/drive', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: true }),
      });
      const s = await apiFetch<GoogleConnectionStatus>('/api/auth/google/status');
      setGoogle(s);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'scope_missing') {
        return connectGoogle();
      }
    } finally {
      setBusy(false);
    }
  }

  async function disconnectDrive() {
    if (!confirm(t('google:drive_disconnect_confirm'))) return;
    setBusy(true);
    try {
      await apiFetch('/api/auth/google/drive', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: false }),
      });
      const s = await apiFetch<GoogleConnectionStatus>('/api/auth/google/status');
      setGoogle(s);
    } finally {
      setBusy(false);
    }
  }

  async function disconnectWa() {
    if (!confirm(t('wa:disconnect_confirm'))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/firms/${firmId}/wa`, { method: 'DELETE' });
      const w = await apiFetch<WaSessionInfo>(`/api/firms/${firmId}/wa/status`);
      setWaInfo(w);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Master toggle "Enable notifications" — triggers the browser
   * permission prompt + registers the device with our backend. We only
   * call this from explicit user action in /settings; the SPA boot
   * never auto-prompts.
   */
  async function loadDevices() {
    try {
      const list = await apiFetch<NotificationDevice[]>('/api/notifications/devices');
      setDevices(list);
    } catch {
      setDevices([]);
    }
  }

  async function removeDevice(id: string) {
    setBusy(true);
    try {
      await apiFetch(`/api/notifications/devices/${id}`, { method: 'DELETE' });
      const prefs = await apiFetch<NotificationPrefs>('/api/notifications/preferences');
      setNotifPrefs(prefs);
      await loadDevices();
    } catch (e) {
      setMsg((e as Error)?.message ?? t('notifications.errors.enable_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function enableNotifs() {
    setBusy(true);
    setMsg(null);
    try {
      const id = await enableNotifications();
      if (id) {
        const prefs = await apiFetch<NotificationPrefs>('/api/notifications/preferences');
        setNotifPrefs(prefs);
        setNotifPermission('granted');
        setMsg(t('notifications.registered'));
        await loadDevices();
      } else {
        const perm = await browserPermission();
        setNotifPermission(perm);
      }
    } catch (e) {
      setMsg((e as Error)?.message ?? t('notifications.errors.enable_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function startErase() {
    setBusy(true);
    setEraseError(null);
    try {
      await apiFetch('/api/me/erase/start', { method: 'POST', body: JSON.stringify({}) });
      setErasePhase('code');
      setMsg(t('erase.code_sent'));
    } catch (e) {
      const msgKey =
        e instanceof ApiError && e.code === 'already_erased'
          ? 'erase.errors.already_erased'
          : 'erase.errors.send_failed';
      setEraseError(t(msgKey));
    } finally {
      setBusy(false);
    }
  }

  async function confirmErase() {
    setBusy(true);
    setEraseError(null);
    try {
      await apiFetch('/api/me/erase/confirm', {
        method: 'POST',
        body: JSON.stringify({ code: eraseCode.trim() }),
      });
      // Success — sign out (Firebase user is now disabled, the cached
      // id token will start failing on its next /api/* anyway).
      setMsg(t('erase.success'));
      setErasePhase('idle');
      setTimeout(() => {
        void signOut();
        window.location.replace('/');
      }, 800);
    } catch (e) {
      if (e instanceof ApiError) {
        const code = e.code as
          | 'wrong_code'
          | 'no_code'
          | 'already_erased'
          | 'invalid_code_format'
          | undefined;
        if (code) setEraseError(t(`erase.errors.${code}`));
        else setEraseError(t('erase.errors.send_failed'));
      } else {
        setEraseError(t('erase.errors.send_failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendTestNotif() {
    setBusy(true);
    setMsg(null);
    setLastTest(null);
    try {
      const res = await sendTestNotification();
      setLastTest(res);
      setMsg(t('notifications.test_sent', { count: res.recipients }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_devices') {
        setMsg(t('notifications.errors.no_devices'));
      } else {
        setMsg((e as Error)?.message ?? t('notifications.errors.test_failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function disableNotifs() {
    setBusy(true);
    setMsg(null);
    try {
      // disableNotifications now does both halves: SDK opt-out (if
      // loaded) AND a server-side wipe of every device row this user
      // owns. The wipe is the authoritative step — without it the
      // device row sticks around and the UI stays in the "active on
      // N devices" state forever.
      await disableNotifications();
      const prefs = await apiFetch<NotificationPrefs>('/api/notifications/preferences');
      setNotifPrefs(prefs);
      setDevices([]);
      setMsg(t('notifications.disabled'));
    } catch (e) {
      setMsg((e as Error)?.message ?? t('notifications.errors.enable_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleNotif(key: NotificationKey, value: boolean) {
    setBusy(true);
    setMsg(null);
    // Optimistic update
    setNotifPrefs((prev) => (prev ? { ...prev, [key]: value } : prev));
    try {
      const updated = await apiFetch<Omit<NotificationPrefs, 'deviceCount'>>(
        '/api/notifications/preferences',
        { method: 'PATCH', body: JSON.stringify({ [key]: value }) },
      );
      setNotifPrefs((prev) =>
        prev ? { ...prev, ...updated } : { ...updated, deviceCount: 0 },
      );
    } catch {
      // Revert on failure
      setNotifPrefs((prev) => (prev ? { ...prev, [key]: !value } : prev));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="full-panel">
        <div className="panel-header">{t('appearance.title')}</div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className="metric-label" style={{ marginBottom: 8 }}>{t('appearance.theme')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {THEMES.map((th) => (
                <button
                  key={th}
                  type="button"
                  className={`type-btn ${theme === th ? 'active' : ''}`}
                  onClick={() => pickTheme(th)}
                  disabled={busy}
                  style={{ flex: 'unset', padding: '8px 16px' }}
                >
                  {th === 'system' && '🖥 '}
                  {th === 'light' && '☀ '}
                  {th === 'dark' && '🌙 '}
                  {t(`appearance.themes.${th}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="metric-label" style={{ marginBottom: 8 }}>{t('appearance.language')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['he', 'en'] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`type-btn ${i18n.language === lang ? 'active' : ''}`}
                  onClick={() => pickLocale(lang)}
                  disabled={busy}
                  style={{ flex: 'unset', padding: '8px 16px' }}
                >
                  {lang === 'he' ? 'עברית' : 'English'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('integrations.title')}</div>
        <IntegrationRow
          icon="📅"
          title={t('integrations.google_title')}
          subtitle={
            google?.connected
              ? t('google:connected_as', { email: google.email ?? '' })
              : t('integrations.google_subtitle')
          }
          actionLabel={
            google?.connected ? t('google:disconnect') : t('google:connect')
          }
          onAction={google?.connected ? disconnectGoogle : connectGoogle}
          busy={busy}
        />
        <IntegrationRow
          icon="👥"
          title={t('integrations.google_contacts_title')}
          subtitle={
            google?.contactsActive
              ? t('google:connected_as', { email: google.email ?? '' })
              : t('integrations.google_contacts_subtitle')
          }
          actionLabel={
            google?.contactsActive
              ? t('google:contacts_disconnect')
              : t('google:contacts_connect')
          }
          onAction={google?.contactsActive ? disconnectContacts : connectContacts}
          busy={busy}
        />
        <IntegrationRow
          icon="📁"
          title={t('integrations.google_drive_title')}
          subtitle={
            google?.driveActive
              ? t('google:connected_as', { email: google.email ?? '' })
              : t('integrations.google_drive_subtitle')
          }
          actionLabel={
            google?.driveActive
              ? t('google:drive_disconnect')
              : t('google:drive_connect')
          }
          onAction={google?.driveActive ? disconnectDrive : connectDrive}
          busy={busy}
        />
        <IntegrationRow
          icon="💬"
          title="WhatsApp"
          subtitle={
            waInfo?.status === 'connected'
              ? t('wa:connected_as', { phone: waInfo.pairedPhone ?? '' })
              : t('integrations.wa_subtitle')
          }
          actionLabel={
            waInfo?.status === 'connected' ? t('wa:disconnect') : t('wa:connect')
          }
          onAction={waInfo?.status === 'connected' ? disconnectWa : onConnectWa}
          busy={busy}
        />
        <IntegrationRow
          icon="✉"
          title={t('email:domain.title')}
          subtitle={t('email:domain.subtitle')}
          actionLabel={t('integrations.manage')}
          onAction={() => setEmailDomainOpen(true)}
          busy={busy}
        />
        <IntegrationRow
          icon="🔑"
          title={t('passkey:title')}
          subtitle={t('passkey:subtitle')}
          actionLabel={t('integrations.manage')}
          onAction={() => setPasskeyOpen(true)}
          busy={busy}
        />
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('notifications.title')}</div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {notifPermission === 'unsupported' ? (
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              {t('notifications.unsupported')}
            </div>
          ) : notifPermission === 'denied' ? (
            <div style={{ fontSize: 13, color: 'var(--red)' }}>
              {t('notifications.denied')}
            </div>
          ) : (notifPrefs?.deviceCount ?? 0) === 0 ? (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
                {t('notifications.enable_hint')}
              </div>
              <button className="btn-primary" onClick={enableNotifs} disabled={busy}>
                🔔 {t('notifications.enable')}
              </button>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                {t('notifications.enabled_on_devices', { count: notifPrefs?.deviceCount ?? 0 })}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {NOTIFICATION_KEYS.map((k) => (
                  <label
                    key={k}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!notifPrefs?.[k]}
                      onChange={(e) => void toggleNotif(k, e.target.checked)}
                      disabled={busy}
                    />
                    {t(`notifications.triggers.${k}`)}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn-secondary" onClick={sendTestNotif} disabled={busy}>
                  🔔 {t('notifications.send_test')}
                </button>
                <button className="btn-cancel" onClick={disableNotifs} disabled={busy}>
                  {t('notifications.disable')}
                </button>
              </div>
              {lastTest && (
                <div style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div>
                    {t('notifications.test_stats', {
                      recipients: lastTest.recipients,
                      failed: lastTest.failed,
                      errored: lastTest.errored,
                    })}
                  </div>
                  {lastTest.recipients > 0 && lastTest.failed === 0 && lastTest.errored === 0 && (
                    <div style={{ color: 'var(--text3)' }}>
                      {t('notifications.test_warning_foreground')}
                    </div>
                  )}
                  {lastTest.dashboardUrl && (
                    <a
                      href={lastTest.dashboardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--brand)' }}
                    >
                      {t('notifications.test_dashboard_link')}
                    </a>
                  )}
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div className="metric-label" style={{ marginBottom: 8 }}>
                  {t('notifications.devices_label')}
                </div>
                {devices.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {t('notifications.devices_empty')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {devices.map((d) => (
                      <div
                        key={d.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                          padding: '6px 10px',
                          background: 'var(--bg-soft, transparent)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>
                            {d.deviceType ?? 'web'} · {d.subscriptionIdShort ?? '—'}
                          </div>
                          <div
                            style={{
                              color: 'var(--text3)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {t('notifications.last_seen', {
                              when: new Date(d.lastSeenAt).toLocaleString(i18n.language, {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              }),
                            })}
                            {d.userAgent ? ` · ${shortUA(d.userAgent)}` : ''}
                          </div>
                        </div>
                        <button
                          className="btn-cancel"
                          onClick={() => void removeDevice(d.id)}
                          disabled={busy}
                          aria-label={t('notifications.devices_remove')}
                          title={t('notifications.devices_remove')}
                          style={{ padding: '4px 10px' }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('account.title')}</div>
        <div style={{ padding: 18 }}>
          <div className="metric-label">{t('account.email')}</div>
          <div style={{ fontSize: 14, marginBottom: 24 }}>{fbUser?.email ?? '—'}</div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
            <div className="metric-label" style={{ color: 'var(--red)', marginBottom: 6 }}>
              {t('erase.title')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>
              {t('erase.description')}
            </div>
            <button
              className="btn-cancel"
              onClick={() => setErasePhase('confirm')}
              disabled={busy}
            >
              {t('erase.button')}
            </button>
          </div>
        </div>
      </div>

      {msg && (
        <div style={{ padding: 12, color: 'var(--green)', fontSize: 13 }}>{msg}</div>
      )}

      {emailDomainOpen && <EmailDomainModal onClose={() => setEmailDomainOpen(false)} />}
      {passkeyOpen && <PasskeyModal onClose={() => setPasskeyOpen(false)} />}
      {erasePhase !== 'idle' && (
        <div className="legal-modal-bg" onClick={() => !busy && setErasePhase('idle')}>
          <div
            className="legal-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440 }}
          >
            <h2 style={{ color: 'var(--red)', marginTop: 0 }}>{t('erase.confirm_title')}</h2>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>
              {erasePhase === 'confirm' ? t('erase.confirm_text') : t('erase.code_sent')}
            </p>
            {erasePhase === 'code' && (
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{ display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--text2)' }}
                >
                  {t('erase.code_label')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  value={eraseCode}
                  onChange={(e) => setEraseCode(e.target.value.replace(/\D/g, ''))}
                  placeholder={t('erase.code_placeholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 18,
                    letterSpacing: 6,
                    textAlign: 'center',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    background: 'var(--surface)',
                    color: 'var(--text)',
                  }}
                  disabled={busy}
                />
              </div>
            )}
            {eraseError && (
              <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{eraseError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="btn-cancel"
                onClick={() => {
                  setErasePhase('idle');
                  setEraseCode('');
                  setEraseError(null);
                }}
                disabled={busy}
              >
                {t('erase.cancel')}
              </button>
              {erasePhase === 'confirm' ? (
                <button className="btn-primary" onClick={startErase} disabled={busy}>
                  {t('erase.send_code')}
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={confirmErase}
                  disabled={busy || eraseCode.length !== 6}
                  style={{ background: 'var(--red)' }}
                >
                  {t('erase.confirm_button')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IntegrationRow({
  icon,
  title,
  subtitle,
  actionLabel,
  onAction,
  busy,
}: {
  icon: string;
  title: string;
  subtitle: string;
  actionLabel: string;
  onAction: () => void;
  busy: boolean;
}) {
  return (
    <div className="case-row">
      <div style={{ fontSize: 24, width: 32, textAlign: 'center' }}>{icon}</div>
      <div className="case-info">
        <div className="case-name">{title}</div>
        <div className="case-sub">{subtitle}</div>
      </div>
      <button type="button" className="btn-cancel" onClick={onAction} disabled={busy}>
        {actionLabel}
      </button>
    </div>
  );
}
